import { http } from './http'
import { Pagination, PaginationParams, PatchPayload } from './map'
import { Vouchers, VouchersPayload } from '../../common/models'
import { DownloadVouchers } from '../../common/models/rewards'

export type VoucherPaginationParams = {
  page?: number
  size?: number
  sortBy?: string
  sortDirection?: 'ASC' | 'DESC'
  startDate?: number
  endDate?: number
  requestType?: 'GENERAL' | 'ONT_TO_ONE'
  voucherCode?: string | null
  redeemCode?: string | null
}

export const generateCode = async () =>
  http.get<string>('/operations/vouchers/generate-code')

export const saveVoucher = async (voucher: VouchersPayload) =>
  http.post<Vouchers>('/operations/vouchers', voucher)

export const createBatchVoucher = async (voucher: VouchersPayload) =>
  http.post<Vouchers>('/operations/vouchers/batch', voucher)

export const getBatchVoucher = async (params?: PaginationParams) =>
  http.get<Pagination<DownloadVouchers>>('/operations/vouchers/batch', {
    params,
  })

export const updateVoucher = async (voucher: VouchersPayload) =>
  http.put<Vouchers>(`/operations/vouchers/${voucher.id}`, voucher)

export const getVouchers = (params?: VoucherPaginationParams) =>
  http.get<Pagination<Vouchers>>('/operations/vouchers', {
    params,
  })

export const deleteVouchers = (data: PatchPayload) =>
  http.delete('/operations/vouchers', { data })

export const downloadVoucherSingle = (
  id: string,
  type: 'PDF' | 'PNG' | 'SVG' | 'CSV',
) =>
  http.get(`/operations/vouchers/${id}/qr-code?format=${type}`, {
    responseType: 'blob',
  })

export const downloadVoucherBatch = (
  id: string,
  type: 'PDF' | 'PNG' | 'SVG' | 'CSV',
) =>
  http.get(`/operations/vouchers/batch/${id}/qr-code?format=${type}`, {
    responseType: 'blob',
  })

export const getVoucherById = async (id: string) =>
  http.get<Vouchers>(`/operations/vouchers/${id}`)

export const vouchersHistory = (
  params?: Omit<VoucherPaginationParams, 'requestType'>,
) =>
  http.get<Pagination<Vouchers>>('/operations/vouchers/history', {
    params,
  })

export const exportVouchersHistory = (
  params?: Omit<VoucherPaginationParams, 'requestType'>,
) =>
  http.get<string>('/operations/vouchers/history/export', {
    params,
  })

export const transferTicketVouchers = async (voucher: {
  toTicketId: string
  fromTicketId: string
  voucherIds: string[]
}) =>
  http.post<void>(
    `/operations/ticket/${voucher.fromTicketId}/vouchers/transfer/${voucher.toTicketId}`,
    { voucherIds: voucher.voucherIds },
  )

export type CoinVoucher = {
  id: string
  label: string
  amount: number
  unlimited: boolean
  voucherLimit: number
  usages: number
  nominated: boolean
  forEmail: string
  redeemCode: string
  createdAt: number
  published: boolean
  expirationDate: number
}

export const coinVouchers = (params?: VoucherPaginationParams) =>
  http.get<Pagination<CoinVoucher>>('/operations/coin-vouchers', {
    params,
  })

export const getCoinVoucherById = (id: string) =>
  http.get<CoinVoucher>(`/operations/coin-vouchers/${id}`)

export type UpdateCoinVoucherPayload = {
  label: string
  expirationDate: number
  published: boolean
}

export const updateCoinVoucher = (
  id: string,
  payload: UpdateCoinVoucherPayload,
) => http.put<CoinVoucher>(`/operations/coin-vouchers/${id}`, payload)

export const deleteCoinVoucher = (id: string) =>
  http.put(`/operations/coin-vouchers/${id}/cancel`)

export type CoinVouchersBatchPayload = {
  label: string
  locale: string
  nrOfVouchers: number
  amount: number
  nominated: boolean
  unlimited: boolean
  voucherLimit: number
  expirationDate: number
  unPublishDate: number
  nominateType: 'UPLOAD' | 'MANUAL'
  uploadKey: string
  emails: string[]
  generateType: 'DOWNLOAD' | 'SEND_EMAIL'
  receiverEmail: string
}

export type CoinVouchersBatchResponse = {
  id: string
  label: string
  amount: string
  zipLink: string
  createdAt: number
  password: string
  status: 'COMPLETED' | 'PROCESSING' | 'FAILED' | 'CANCELED'
}

export const coinVouchersBatch = (payload: CoinVouchersBatchPayload) =>
  http.post<CoinVouchersBatchResponse>(
    '/operations/coin-vouchers/batch',
    payload,
  )

export type CoinVouchersEmailUploadResponse = {
  uploadKey: string
  successfullyValidated: number
  failedEmails: string[]
}

export const coinVouchersEmailUpload = (file: File) => {
  const formData = new FormData()
  formData.append('csv', file)
  return http.post<CoinVouchersEmailUploadResponse>(
    '/operations/coin-vouchers/email-upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
}

export const getCoinVoucherPdf = (id: string) =>
  http.get(`/operations/coin-vouchers/${id}/pdf`, {
    responseType: 'blob',
  })

export type CoinVouchersBatchDownloadListResponse = {
  id: string
  label: string
  amount: string
  zipLink: string
  createdAt: number
  password: string
  status: 'COMPLETED' | 'PROCESSING' | 'FAILED' | 'CANCELED'
}

export const coinVouchersBatchListDownload = (
  params?: VoucherPaginationParams,
) =>
  http.get<Pagination<CoinVouchersBatchDownloadListResponse>>(
    '/operations/coin-vouchers/batch',
    {
      params,
    },
  )

export const cancelCoinVoucherBatch = (id: string) =>
  http.put(`/operations/coin-vouchers/batch/${id}/cancel`)
